import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PractitionerCard } from '../components/Listings/PractitionerCard';
import { SkeletonGrid } from '../components/ui/SkeletonCard';
import { HorizontalFilters } from '../components/Practitioners/Filters/HorizontalFilters';
import { PractitionerForm } from '../components/Listings/Forms/PractitionerForm';
import { Hero } from '../components/Hero';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';
import { europeanCountries } from '../lib/constants/countries';
import { getCurrentLocation, filterByDistance, type LocationCoords } from '../lib/utils/locationFilter';
import { Search, MapPin } from 'lucide-react';
import type { Practitioner } from '../types/practitioners';

interface Filters {
  categories: string[];
  countries: string[];
  languages: string[];
  workArrangements: string[];
  corporateWellness: boolean;
  nearMe: boolean;
}

export function Practitioners() {
  const PAGE_SIZE = 24;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [filteredPractitioners, setFilteredPractitioners] = useState<(Practitioner & { distance?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userLocation, setUserLocation] = useState<LocationCoords | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    categories: [],
    countries: [],
    languages: [],
    workArrangements: [],
    corporateWellness: false,
    nearMe: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const userHasInteracted = useRef(false);
  const searchQueryParam = searchParams.get('q')?.trim() || '';
  const locationParamRaw = searchParams.get('location')?.trim() || '';
  const locationKeyword = locationParamRaw.split(',')[0]?.trim() || locationParamRaw;

  // Initialize filters from URL parameters
  useEffect(() => {
    const countryParam = searchParams.get('country');

    if (countryParam) {
      setFilters(prev => ({
        ...prev,
        countries: [countryParam]
      }));
    }

    setSearchQuery(searchQueryParam);
  }, [searchParams]);

  useEffect(() => {
    setPage(0);
    setPractitioners([]);
  }, [filters, searchParams, refreshKey]);

  useEffect(() => {
    async function fetchPractitioners() {
      try {
        if (page === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        // Only use URL parameter if user hasn't interacted with filters yet
        const countryParam = searchParams.get('country');
        const effectiveFilters = (countryParam && !userHasInteracted.current)
          ? { ...filters, countries: [countryParam] }
          : filters;

        let query = supabase
          .from('practitioners')
          .select(`
            *,
            user:users (
              id,
              username,
              full_name,
              avatar_url,
              verified,
              user_type,
              subscription_status,
              manual_professional
            )
          `)
          .eq('approval_status', 'approved')
          .eq('user.user_type', 'professional')
          .eq('user.subscription_status', 'active');

        if (effectiveFilters.categories.length > 0) {
          query = query.in('category', effectiveFilters.categories);
        }
        if (effectiveFilters.countries.length > 0) {
          const expandedCountries = Array.from(new Set(
            effectiveFilters.countries.flatMap((c) => {
              const match = europeanCountries.find(ec => ec.value === c || ec.label === c);
              return match ? [match.value, match.label] : [c];
            })
          ));
          query = query.in('country', expandedCountries);
        }
        if (locationKeyword) {
          query = query.or(`address.ilike.%${locationKeyword}%,country.ilike.%${locationKeyword}%`);
        }
        if (effectiveFilters.languages.length > 0) {
          // Convert comma-separated language string to array and check overlap
          query = query.filter('language', 'ilike', `%${effectiveFilters.languages[0]}%`);
          // Add additional language filters if more than one selected
          effectiveFilters.languages.slice(1).forEach(lang => {
            query = query.or(`language.ilike.%${lang}%`);
          });
        }
        if (effectiveFilters.workArrangements.length > 0) {
          // Enhanced logic: if someone selects "In_Person" or "Online", also show "Hybrid" practitioners
          const workArrangementFilters = [...effectiveFilters.workArrangements];

          // If "In_Person" is selected, also include "Hybrid"
          if (effectiveFilters.workArrangements.includes('In_Person') && !effectiveFilters.workArrangements.includes('Hybrid')) {
            workArrangementFilters.push('Hybrid');
          }

          // If "Online" is selected, also include "Hybrid"
          if (effectiveFilters.workArrangements.includes('Online') && !effectiveFilters.workArrangements.includes('Hybrid')) {
            workArrangementFilters.push('Hybrid');
          }

          query = query.in('work_arrangement', workArrangementFilters);
        }
        if (effectiveFilters.corporateWellness) {
          query = query.eq('corporate_wellness', true);
        }

        query = query
          .order('created_at', { ascending: false })
          .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

        const { data, error } = await query;
        if (error) throw error;

        const rows = data || [];
        setHasMore(rows.length === PAGE_SIZE);
        setPractitioners(prev => page === 0 ? rows : [...prev, ...rows]);
      } catch (err: any) {
        setError(err.message);
      } finally {
        if (page === 0) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }

    fetchPractitioners();
  }, [filters, searchParams, refreshKey, page]);

  // Apply Near Me filter and search query client-side
  useEffect(() => {
    let filtered = [...practitioners];

    // Apply search query filter
    const effectiveSearchQuery = (searchQuery || searchQueryParam).trim();
    if (effectiveSearchQuery) {
      const query = effectiveSearchQuery.toLowerCase();
      filtered = filtered.filter(practitioner =>
        practitioner.title?.toLowerCase().includes(query) ||
        practitioner.description?.toLowerCase().includes(query) ||
        practitioner.category?.toLowerCase().includes(query) ||
        practitioner.helps_with?.toLowerCase().includes(query)
      );
    }

    if (locationKeyword) {
      const keyword = locationKeyword.toLowerCase();
      filtered = filtered.filter(practitioner =>
        practitioner.address?.toLowerCase().includes(keyword) ||
        practitioner.country?.toLowerCase().includes(keyword)
      );
    }

    // Apply Near Me filter
    if (filters.nearMe && userLocation) {
      filtered = filterByDistance(filtered, userLocation, 100); // 100km radius
    }

    setFilteredPractitioners(filtered);
  }, [practitioners, filters.nearMe, userLocation, searchQuery, searchQueryParam, locationKeyword]);

  const handleFilterChange = async (filterType: keyof Filters, values: string[] | boolean) => {
    // Mark that user has interacted with filters
    userHasInteracted.current = true;

    // Handle Near Me filter specially
    if (filterType === 'nearMe' && values === true && !userLocation) {
      setLocationLoading(true);
      try {
        const location = await getCurrentLocation();
        setUserLocation(location);
        setFilters(prev => ({ ...prev, nearMe: true }));
      } catch (err: any) {
        setError(err.message);
        setFilters(prev => ({ ...prev, nearMe: false }));
      } finally {
        setLocationLoading(false);
      }
      return;
    }

    setFilters(prev => ({
      ...prev,
      [filterType]: values
    }));
  };

  const handleAddressSelect = (latitude: number, longitude: number, address: string) => {
    setUserLocation({ latitude, longitude });
    setFilters(prev => ({ ...prev, nearMe: true }));
  };

  const handleCreateListing = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      window.dispatchEvent(new CustomEvent('show-auth'));
    } else {
      setShowCreateModal(true);
    }
  };

  const displayPractitioners = filteredPractitioners;

  return (
    <div>
      <Meta
        title={`${t('practitioners.title')} | Mindful Family`}
        description={t('practitioners.subtitle')}
      />

      {/* Mobile Full-Width Header */}
      <div className="lg:hidden relative h-64 overflow-hidden">
        <img
          src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-practitioner-soundbath.png"
          alt="Find Wellness Practitioners"
          className="w-full h-full object-cover object-center shadow-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
      </div>

      {/* Mobile Content Below Image */}
      <div className="lg:hidden px-4 py-8 text-center bg-gray-50">
        <h1 className="text-3xl font-gelica font-bold text-content mb-4">
          {t('practitioners.title')}
        </h1>
        <p className="text-lg text-content/70 mb-6 max-w-md mx-auto">
          {t('practitioners.subtitle')}
        </p>
        <div className="mb-3 px-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('practitioners.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-12 py-3 rounded-full border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#8DA847]/20 focus:border-[#8DA847] shadow-sm"
            />
            <button
              onClick={() => handleFilterChange('nearMe', !filters.nearMe)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${
                filters.nearMe
                  ? 'bg-[#8DA847] text-white'
                  : 'text-gray-400 hover:text-[#8DA847] hover:bg-[#8DA847]/10'
              }`}
              title={filters.nearMe ? 'Disable Near Me' : 'Enable Near Me'}
            >
              <MapPin className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div>
          <button
            onClick={handleCreateListing}
            className="text-sm text-accent-text hover:text-accent-text/80 underline"
          >
            {t('practitioners.areYouPractitioner')}
          </button>
        </div>
      </div>

      {/* Desktop Hero */}
      <div className="hidden lg:block relative mb-12 bg-gradient-to-b from-[#F3F7EE] via-[#F8FAF5] to-white overflow-hidden">
        <div className="py-8 lg:py-16 relative z-10">
          <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-4 lg:mb-6">{t('practitioners.title')}</h1>
                <p className="text-lg lg:text-xl text-gray-600 mb-6 lg:mb-8">{t('practitioners.subtitle')}</p>
                <div className="mb-3">
                  <div className="relative max-w-lg">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('practitioners.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 rounded-full border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#8DA847]/20 focus:border-[#8DA847] shadow-sm"
                    />
                    <button
                      onClick={() => handleFilterChange('nearMe', !filters.nearMe)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${
                        filters.nearMe
                          ? 'bg-[#8DA847] text-white'
                          : 'text-gray-400 hover:text-[#8DA847] hover:bg-[#8DA847]/10'
                      }`}
                      title={filters.nearMe ? 'Disable Near Me' : 'Enable Near Me'}
                    >
                      <MapPin className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div>
                  <button
                    onClick={handleCreateListing}
                    className="text-sm text-accent-text hover:text-accent-text/80 underline"
                  >
                    {t('practitioners.areYouPractitioner')}
                  </button>
                </div>
              </div>
      <div className="hidden lg:block">
                <img
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-practitioner-soundbath.png"
                  alt="Find Wellness Practitioners"
                  className="w-full h-[450px] object-cover blob-shape"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-0 sm:px-6 lg:px-10 mt-16 lg:mt-8" data-filters-section>
          <HorizontalFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            locationLoading={locationLoading}
            onAddressSelect={handleAddressSelect}
            showExpanded={true}
          />

        <div className="max-w-page mx-auto">

          <div className="px-4 sm:px-0 mt-6">
          {error && (
            <div className="text-red-600 mb-4">{error}</div>
          )}

          {loading ? (
            <SkeletonGrid count={6} />
          ) : displayPractitioners.length === 0 ? (
            <div className="bg-background rounded-lg p-8 text-center">
              <p className="text-content">
                {searchQuery
                  ? `No practitioners found for "${searchQuery}". Try a different search term.`
                  : filters.nearMe ? t('practitioners.noPractitionersNearby') : t('practitioners.noPractitionersFilter')}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 sm:px-0">
                {displayPractitioners.map((practitioner) => (
                  <PractitionerCard
                    key={practitioner.id}
                    practitioner={practitioner}
                    distance={(practitioner as any).distance}
                  />
                ))}
              </div>
              {hasMore && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => setPage(prev => prev + 1)}
                    disabled={loadingMore}
                    className="px-6 py-2.5 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors text-sm disabled:opacity-60"
                  >
                    {loadingMore ? 'Loading...' : 'Show more'}
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <PractitionerForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
}
